import { WeeklyReportConfigType } from "components/ReportsPage/reportPageTypes";
import { SummaryMetricInput } from "reports/utils/metricUtils";
import { PnlCategoriesEnum, ProducingLossesEnum } from "reports/types";

/**
 *  If a customer has a report page and have reports turned on in flightConfig,
 *  the configs for the report times are indicated here.
 *
 *  If startTime exists, they will have EOS reports.
 *  If dailyReportTimes exists, they will have daily reports
 *  If weeklyReportTimes exists, they will have weekly reports.
 *
 *  All hours/minutes should be in the customer's timezone (indicated by the timezone property)
 */

export type ReportsConfig = {
  /** When this customer was deployed, or certain configs for valid data to be showing on reports
   * This is only needed if they are using the ReportsPage.tsx
   * (in milliseconds)
   */
  startTime?: number;
  /** set to true if customer just wants daily or weekly */
  shouldSkipEOS?: boolean;
  /** true by default, set to false if we want to see Not Schedule shift reports */
  shouldSkipNPS?: boolean;
  /** list of machineGroups to show reports for */
  machineGroups?: string[];
  /** used in useReportsPropsList to determine displayed report time range */
  timeZone?: string;
  /** @deprecated use timeZone instead */
  timezone?: string;
  /** false by default, groups dropdown by machine group */
  shouldGroupByMachineGroup?: boolean;
  dailyReportConfig?: {
    startHour: number;
    startMinute: number;

    /** if not included, use startHour */
    endHour?: number;
    /** if not included, use startMinute */
    endMinute?: number;

    /** list of machineGroups to show daily reports, if not included use machineGroups */
    machineGroups?: string[];
    /** use custom report title if supplied */
    reportTitle?: string;
  };
  weeklyReportConfig?: WeeklyReportConfigType;
  performanceReportConfig?: {
    /** Performance Report are of two types : Weekly and Monthly */
    weekly?: WeeklyReportConfigType;
  };
  /** CSS applied to reports pages */
  customCss?: string;
  /** configure the EOSReport */
  eosReportMetricsConfig: EosReportMetricsConfig | null;
  eosReportGeneralConfig: EosReportGeneralConfig | null;
};

export type ReportSummaryMetric = SummaryMetricInput & {
  /** only show on report pages with specified processGroupName (name in url) */
  onlyShowForGroupIds?: string[];
  /** only show in specified charts, options are ["shiftBreakdown", "pnlBreakdown"] */
  onlyShowInCharts?: string[];
  /** hide on report pages with specified processGroupName (name in url) */
  hideForGroupIds?: string[];
  /** override default red/green target-based color in Process Breakdown chart with standard colors e.g. "black", "rgb(0,0,0)" */
  color?: string;
  /** dataDumpKey is used to fetch the description when the metric is returned from midgard */
  dataDumpKey?: string;
  units?: string;
  displayName?: string;
  /** (⚠ increases server usage) fetch a separate aggregate specifically for this metric; only fetch these machemetryIds */
  overrideMachemetryIds?: string[];
  /** create multiple copies of this metric with different properties -> if a metric has "instances" key, we create one metric for each instance, each instance specifies its property overrides
   * e.g.
   * instances: [
   *   { onlyShowForGroupIds: "Team A", displayName: "TeamAOverridenDisplayName" },
   *   { overrideMachemetryIds: ["raveng_d_001"], "onlyShowOnPages": ["hublet"] }
   * ] */
  instances?: Partial<ReportSummaryMetric>[];
};

export type EosReportMetricsConfig = {
  /** displayed value (generated in GroupMetrics > calcMetric)*/
  summaryMetricsRow1: Partial<ReportSummaryMetric>[];
  summaryMetricsRow2: Partial<ReportSummaryMetric>[];
  summaryMetricsInputList: Partial<ReportSummaryMetric>[];
};
export type EosReportGeneralConfig = {
  shouldShowPrint: boolean;
  shouldShowTranslate?: boolean;
  shouldSkipSkuData: boolean;
  shouldUseProducingLosses: boolean;
  shouldHideDownload?: boolean;
  producingLossesToSkip?: ProducingLossesEnum[];
  machemetryIdsToExclude?: string[];
  /** appears in first row of report header */
  customHeaderTitle?: string;
  /** appears in second row of report header */
  customReportTitle?: string;
  /** show `${customTitlePrefix} - ${processGroupName}` in report header (overrides `customReportTitle`) */
  customTitlePrefix?: string;
  customTitlePrefixByGroup?: { [processGroupName: string]: string };
  flightTimelineLinkGroup?: FlightTimelineLinkGroup;
  flightDashboardLinkGroup?: FlightTimelineLinkGroup;
  topDowntimesReports?: {
    enabled: boolean;
    reportRanges: ReportRangeEnum[];
    customTitle?: string;
    categoriesToSkip?: PnlCategoriesEnum[];
    numberOfDowntimes?: number;
    shouldShowHideLessThan1Percent?: boolean;
    xLabel?: string;
    timeDivisorMs?: number;
    showDurationLabel?: boolean;
  }[];
  trendChart?: TrendChartConfig;
  /** show "view report" button in MetricAndPnLBreakdowns component e.g. Shift Breakdowns chart rows */
  showReportLinks?: boolean | (ReportRangeEnum | ReportRangeLinksEnum)[];
  shouldShowAvgDuration?: boolean;
  /** enable readable dates on the downloaded CSV via CSVDownloadButton */
  csvDownloadEnableReadableDates?: boolean;
};
export enum ReportRangeEnum {
  "shift" = "shift",
  "day" = "day",
  "week" = "week",
}
/** used to show 'view timeline' menu item next to each process breakdown > sku breakdown row */
export enum ReportRangeLinksEnum {
  "shiftSku" = "shiftSku",
  "daySku" = "daySku",
  "weekSku" = "weekSku",
}
type FlightTimelineLinkGroup = {
  processGroupName: string;
  onlyShowForRanges?: ReportRangeEnum[];
};

type TrendChartConfig = {
  summaryMetricsInputList: Partial<ReportSummaryMetric>[];
  descendingOrder: boolean;
};
